/** @jsxImportSource @emotion/react */
"use client";

import { useDebouncedValue } from "@packages/sdk";
import { useCallback, useEffect, useRef, useState } from "react";

import type { ComboboxOptionsResult } from "./comboboxAsyncPaging";
import { mergeOptionPages, toOptionsPage } from "./comboboxAsyncPaging";
import type { ComboboxSearchProps } from "./ComboboxSearch.emotion";
import { ComboboxSearch } from "./ComboboxSearch.emotion";
import type { ComboboxOption } from "./types";

export type { ComboboxOption } from "./types";
export type {
  ComboboxOptionsPage,
  ComboboxOptionsResult,
} from "./comboboxAsyncPaging";

export interface ComboboxSearchAsyncConfig {
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number;
  /** Minimum characters before triggering search (default: 0) */
  minSearchLength?: number;
  /** Whether to fetch options on mount (default: true) */
  fetchOnMount?: boolean;
  /** Keep previous results visible while loading new ones (default: true) */
  preserveResultsWhileLoading?: boolean;
}

export interface ComboboxSearchAsyncProps<T = string>
  extends Omit<
    ComboboxSearchProps<T>,
    "options" | "isLoading" | "isLoadingMore" | "hasMore" | "onLoadMore"
  > {
  /** Override for default options, in case displaying the raw value doesn't work */
  defaultOptions?: ComboboxOption<T>[];
  /** Receives the search string and the zero-based page to fetch; return `{ options, hasMore }` for infinite scroll, or a bare array for a single page */
  getOptions: (
    search: string,
    page: number,
  ) => Promise<ComboboxOptionsResult<T>>;
  /** Async-specific configuration */
  asyncConfig?: ComboboxSearchAsyncConfig;
}

interface SearchPageCacheEntry<T> {
  options: ComboboxOption<T>[];
  hasMore: boolean;
  nextPage: number;
}

export function ComboboxSearchAsync<T = string>({
  defaultOptions,
  getOptions,
  asyncConfig = {},
  inputValue: controlledInputValue,
  onInputChange,
  config,
  ...props
}: ComboboxSearchAsyncProps<T>) {
  const {
    debounceMs = 300,
    minSearchLength = 0,
    fetchOnMount = true,
    preserveResultsWhileLoading = true,
  } = asyncConfig;
  const [internalInputValue, setInternalInputValue] = useState(
    props.value?.toString() ?? "",
  );
  const [options, setOptions] = useState<ComboboxOption<T>[]>(
    defaultOptions ??
      (props.value
        ? [
            {
              value: props.value,
              label: <>{props.value}</>,
              text: props.value.toString(),
            },
          ]
        : []),
  );
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasFetchedOnMount, setHasFetchedOnMount] = useState(false);
  const cachedOptionsRef = useRef<Map<string, SearchPageCacheEntry<T>>>(
    new Map(),
  );
  const isLoadingMoreRef = useRef(false);
  // the debounced-search effect below also fires immediately on mount (its
  // initial value is the starting inputValue, typically ""), duplicating
  // the fetchOnMount effect -- skip its first run in that exact case
  const skipInitialEmptyFetchRef = useRef(fetchOnMount);

  const inputValue =
    controlledInputValue !== undefined
      ? controlledInputValue
      : internalInputValue;

  useEffect(() => {
    if (controlledInputValue !== undefined) return;
    setInternalInputValue(props.value?.toString() ?? "");
  }, [props.value]);

  const handleInputChange = useCallback(
    (newValue: string) => {
      if (onInputChange) {
        onInputChange(newValue);
      } else {
        setInternalInputValue(newValue);
      }
    },
    [onInputChange],
  );

  const debouncedSearch = useDebouncedValue(inputValue, debounceMs);
  // lets a resolved fetch check it's still for the on-screen search before repainting
  const debouncedSearchRef = useRef(debouncedSearch);
  debouncedSearchRef.current = debouncedSearch;

  const applyEntry = useCallback(
    (search: string, entry: SearchPageCacheEntry<T>) => {
      cachedOptionsRef.current.set(search, entry);
      if (search !== debouncedSearchRef.current) return;
      setOptions(entry.options);
      setHasMore(entry.hasMore);
    },
    [],
  );

  useEffect(() => {
    if (!fetchOnMount || hasFetchedOnMount) return;

    setHasFetchedOnMount(true);
    setIsLoading(true);

    getOptions("", 0)
      .then((result) => {
        const page = toOptionsPage(result);
        applyEntry("", {
          options: page.options,
          hasMore: page.hasMore,
          nextPage: 1,
        });
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [fetchOnMount, hasFetchedOnMount, getOptions, applyEntry]);

  useEffect(() => {
    if (skipInitialEmptyFetchRef.current) {
      skipInitialEmptyFetchRef.current = false;
      if (debouncedSearch === "") return;
    }

    if (debouncedSearch.length < minSearchLength) {
      const cached = cachedOptionsRef.current.get("");
      if (cached) {
        setOptions(cached.options);
        setHasMore(cached.hasMore);
      }
      return;
    }

    const cached = cachedOptionsRef.current.get(debouncedSearch);
    if (cached) {
      setOptions(cached.options);
      setHasMore(cached.hasMore);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    getOptions(debouncedSearch, 0)
      .then((result) => {
        if (cancelled) return;
        const page = toOptionsPage(result);
        applyEntry(debouncedSearch, {
          options: page.options,
          hasMore: page.hasMore,
          nextPage: 1,
        });
        setIsLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, minSearchLength, getOptions, applyEntry]);

  const handleLoadMore = useCallback(() => {
    const entry = cachedOptionsRef.current.get(debouncedSearch);
    if (!entry?.hasMore || isLoadingMoreRef.current) return;

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    getOptions(debouncedSearch, entry.nextPage)
      .then((result) => {
        const page = toOptionsPage(result);
        applyEntry(debouncedSearch, {
          options: mergeOptionPages(entry.options, page.options),
          hasMore: page.hasMore,
          nextPage: entry.nextPage + 1,
        });
      })
      .catch(() => {
        // keep the already-loaded list; scrolling again retries the fetch
      })
      .finally(() => {
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      });
  }, [debouncedSearch, getOptions, applyEntry]);

  const showLoading =
    isLoading && (!preserveResultsWhileLoading || options?.length === 0);

  return (
    <ComboboxSearch<T>
      {...props}
      options={options}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      isLoading={showLoading}
      isLoadingMore={isLoadingMore}
      hasMore={hasMore}
      onLoadMore={handleLoadMore}
      config={{ ...config, filterLocally: false }}
    />
  );
}
